import { useState } from 'react';
import { SearchIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { ChipRow } from '@/shared/components/ChipRow';

import {
  FILTER_WORDS,
  LIMITS_FILTERS,
  RUN_FILTERS,
  type LimitsFilter,
} from '../workspaceLimits';

export interface LimitsToolbarProps {
  query: string;
  onQueryChange: (query: string) => void;
  filter: LimitsFilter;
  onFilterChange: (filter: LimitsFilter) => void;
  /** How many rows each filter would keep, before the search narrows them. */
  counts: Record<LimitsFilter, number>;
  /** Without run capacity the run filters have nothing to say (R9). */
  hasRuns: boolean;
}

/**
 * Finding a workspace (spec 117 US2): a name search and one filter at a time, each
 * chip saying how many rows it would keep so a reader knows before pressing it.
 *
 * The search keeps its own draft and reports every keystroke: the address is where the
 * value lives, but typing into a field that re-renders from the router after each
 * navigation loses characters on a fast keyboard.
 */
export function LimitsToolbar({
  query,
  onQueryChange,
  filter,
  onFilterChange,
  counts,
  hasRuns,
}: LimitsToolbarProps) {
  const [draft, setDraft] = useState(query);
  const [lastQuery, setLastQuery] = useState(query);
  // An outside change (Clear, the back button) wins over the draft.
  if (query !== lastQuery) {
    setLastQuery(query);
    setDraft(query);
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <label className="relative min-w-48 flex-1">
        <span className="sr-only">Search workspaces</span>
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
        />
        <Input
          type="search"
          value={draft}
          placeholder="Search workspaces"
          onChange={(event) => {
            setDraft(event.target.value);
            setLastQuery(event.target.value);
            onQueryChange(event.target.value);
          }}
          className="ps-8"
        />
      </label>
      <ChipRow label="Show">
        {LIMITS_FILTERS.filter((f) => hasRuns || !RUN_FILTERS.has(f)).map(
          (option) => {
            const pressed = option === filter;
            // Pressed reads in the foreground on the primary outline, not Light Blue:
            // small text in that colour fails on the dark theme (WD-04).
            return (
              <button
                key={option}
                type="button"
                aria-pressed={pressed}
                onClick={() => {
                  onFilterChange(option);
                }}
                className={`focus-visible:ring-ring touch-target inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none ${
                  pressed
                    ? 'border-primary/50 bg-primary/10 text-foreground'
                    : 'border-border bg-card text-foreground hover:bg-accent/50'
                }`}
              >
                {FILTER_WORDS[option]}{' '}
                <span className="text-muted-foreground tabular-nums">
                  {counts[option]}
                </span>
              </button>
            );
          },
        )}
      </ChipRow>
    </div>
  );
}
